import Button from '@/components/Buttons/Button';
import theme from '@/theme';
import styled from '@emotion/styled';
import { Divider, Popover, TextField, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { INSIGHTS_DATE_RANGE } from '../../types';

export const TimeFilterContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-left: auto;
    gap: 4px;

    padding: 4px;
    border-radius: 8px;
    border: 1px solid ${theme.colors.black[300]};
    background-color: ${theme.colors.white};
`;

export const TimeFilterButton = styled(Button)<{ active: boolean }>`
    padding: 0 12px;
    border-radius: 8px;

    &.MuiButtonBase-root {
        background-color: ${({ active }) => (active ? theme.colors.black[100] : theme.colors.white)};

        &:hover {
            background-color: ${({ active }) => (active ? theme.colors.black[100] : theme.colors.black[50])}!important;
        }
    }
`;

const DateRangePopoverContent = styled.div`
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 280px;
`;

const DateRangeRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const DateLabel = styled(Typography)`
    font-size: 14px;
    font-weight: 500;
    color: ${theme.colors.secondary.main};
`;

const StyledTextField = styled(TextField)`
    & .MuiInputBase-root {
        background: ${theme.colors.white};
        height: 40px;
        cursor: pointer;
    }

    & input {
        padding: 8px 12px;
        font-size: 14px;
        cursor: pointer;
    }

    & .MuiOutlinedInput-notchedOutline {
        border-radius: 8px;
        border-color: ${theme.colors.black[300]};
    }

    &:hover .MuiOutlinedInput-notchedOutline {
        border-color: ${theme.colors.primary[500]};
    }
`;

const DateRangeActions = styled.div`
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    margin-top: 8px;
`;

const DateRangeButton = styled(Button)`
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 14px;
    height: 36px;
    min-width: 80px;
`;

interface TimeRangeFilterProps {
    dateRange: INSIGHTS_DATE_RANGE;
    from: Date;
    to: Date;
    onDateRangeChange: (range: INSIGHTS_DATE_RANGE, customFrom?: Date, customTo?: Date) => void;
}

export const TimeRangeFilter = ({ dateRange, from, to, onDateRangeChange }: TimeRangeFilterProps) => {
    const [dateRangeAnchor, setDateRangeAnchor] = useState<HTMLButtonElement | null>(null);
    const [customStartDate, setCustomStartDate] = useState<string>('');
    const [customEndDate, setCustomEndDate] = useState<string>('');
    const startDateInputRef = useRef<HTMLInputElement>(null);
    const endDateInputRef = useRef<HTMLInputElement>(null);

    const handleTimeRangeChange = (range: INSIGHTS_DATE_RANGE, event?: React.MouseEvent<HTMLButtonElement>) => {
        if (range === INSIGHTS_DATE_RANGE.CUSTOM && event) {
            setDateRangeAnchor(event.currentTarget);
            setCustomStartDate(from.toISOString().split('T')[0]);
            setCustomEndDate(to.toISOString().split('T')[0]);
        } else {
            setDateRangeAnchor(null);
            onDateRangeChange(range);
        }
    };

    const handleApplyCustomDates = () => {
        const fromDate = customStartDate ? new Date(customStartDate) : new Date('1970-01-01');
        fromDate.setHours(0, 0, 0, 0);

        const toDate = customEndDate ? new Date(customEndDate) : new Date();
        toDate.setHours(23, 59, 59, 999);

        onDateRangeChange(INSIGHTS_DATE_RANGE.CUSTOM, fromDate, toDate);
        setDateRangeAnchor(null);
    };

    const handleCancelCustomDates = () => {
        setDateRangeAnchor(null);
    };

    const getCustomDateLabel = () => {
        if (dateRange !== INSIGHTS_DATE_RANGE.CUSTOM) {
            return 'Custom';
        }
        const fromFormatted = from.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const toFormatted = to.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return `${fromFormatted} - ${toFormatted}`;
    };

    return (
        <>
            <TimeFilterContainer>
                <TimeFilterButton
                    active={dateRange === INSIGHTS_DATE_RANGE.LAST_6_MONTHS}
                    onClick={() => handleTimeRangeChange(INSIGHTS_DATE_RANGE.LAST_6_MONTHS)}>
                    6M
                </TimeFilterButton>

                <Divider orientation="vertical" flexItem />

                <TimeFilterButton
                    active={dateRange === INSIGHTS_DATE_RANGE.LAST_30_DAYS}
                    onClick={() => handleTimeRangeChange(INSIGHTS_DATE_RANGE.LAST_30_DAYS)}>
                    30D
                </TimeFilterButton>

                <Divider orientation="vertical" flexItem />

                <TimeFilterButton
                    active={dateRange === INSIGHTS_DATE_RANGE.LAST_7_DAYS}
                    onClick={() => handleTimeRangeChange(INSIGHTS_DATE_RANGE.LAST_7_DAYS)}>
                    7D
                </TimeFilterButton>

                <Divider orientation="vertical" flexItem />

                <TimeFilterButton
                    active={dateRange === INSIGHTS_DATE_RANGE.YESTERDAY}
                    onClick={() => handleTimeRangeChange(INSIGHTS_DATE_RANGE.YESTERDAY)}>
                    Yesterday
                </TimeFilterButton>

                <Divider orientation="vertical" flexItem />

                <TimeFilterButton
                    active={dateRange === INSIGHTS_DATE_RANGE.CUSTOM}
                    onClick={e => handleTimeRangeChange(INSIGHTS_DATE_RANGE.CUSTOM, e)}>
                    {getCustomDateLabel()}
                </TimeFilterButton>
            </TimeFilterContainer>

            <Popover
                open={!!dateRangeAnchor}
                anchorEl={dateRangeAnchor}
                onClose={handleCancelCustomDates}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            marginTop: 1,
                            borderRadius: '8px',
                            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                        },
                    },
                }}>
                <DateRangePopoverContent>
                    <DateRangeRow>
                        <DateLabel>Start Date</DateLabel>
                        <StyledTextField
                            type="date"
                            value={customStartDate}
                            onChange={e => setCustomStartDate(e.target.value)}
                            onClick={() => startDateInputRef.current?.showPicker?.()}
                            inputRef={startDateInputRef}
                            size="small"
                            fullWidth
                        />
                    </DateRangeRow>
                    <DateRangeRow>
                        <DateLabel>End Date</DateLabel>
                        <StyledTextField
                            type="date"
                            value={customEndDate}
                            onChange={e => setCustomEndDate(e.target.value)}
                            onClick={() => endDateInputRef.current?.showPicker?.()}
                            inputRef={endDateInputRef}
                            size="small"
                            fullWidth
                        />
                    </DateRangeRow>
                    <DateRangeActions>
                        <DateRangeButton variant="outlined" color="secondary" onClick={handleCancelCustomDates}>
                            Cancel
                        </DateRangeButton>
                        <DateRangeButton variant="contained" color="primary" onClick={handleApplyCustomDates}>
                            Apply
                        </DateRangeButton>
                    </DateRangeActions>
                </DateRangePopoverContent>
            </Popover>
        </>
    );
};
